import { z } from "zod";
import {
  longTextSchema,
  optionalText,
  plainTimeSchema,
  shortTextSchema,
  ulidSchema,
} from "./primitives.js";

export const COMPETITION_TYPES = ["competitive", "exhibition"] as const;
export const COMPETITION_FORMATS = ["judged", "placement_only", "heat_or_bracket"] as const;
export const COMPETITION_ENTRY_MODES = ["once", "per_session"] as const;

/**
 * How somebody comes to be entered (D75). `chosen` is a contest — you tick it. `automatic` is
 * Grand Entry — registering for anything in a session it runs in enters you. `either` is both,
 * for a community that wants the sweep and still wants a dancer able to name their own category.
 */
export const COMPETITION_ENROLMENTS = ["chosen", "automatic", "either"] as const;
export type CompetitionEnrolment = (typeof COMPETITION_ENROLMENTS)[number];

/**
 * The participant unit (D55). Both default to 1 — an individual competition, which creates no team.
 * `rosterMax > 1` is a team competition. The ceiling is deliberately loose; it is a community's
 * choice, not the platform's, and an unbounded relay is `rosterMax` set high, not a special case.
 */
export const rosterBoundSchema = z.number().int().min(1).max(999);

/** The target panel size for a competition (D56), overriding the event default. Never a cap. */
export const competitionPanelSizeSchema = z.number().int().min(1).max(99);

/** A team competition's roster is coherent only when the floor does not exceed the ceiling. */
const rosterOrdered = (value: { rosterMin?: number | undefined; rosterMax?: number | undefined }) =>
  value.rosterMin === undefined ||
  value.rosterMax === undefined ||
  value.rosterMin <= value.rosterMax;
const rosterOrderIssue = {
  message: "The minimum roster size cannot exceed the maximum",
  path: ["rosterMin"] as PropertyKey[],
};

export const createCompetitionSchema = z
  .object({
    name: shortTextSchema,
    description: optionalText(longTextSchema),
    /** An exhibition pays but is not ranked, so it skips judging and results without tripping the
     *  readiness rules about missing scores (D17). */
    type: z.enum(COMPETITION_TYPES).default("competitive"),
    /** Not every competition is a judged dance category (D19). */
    format: z.enum(COMPETITION_FORMATS).default("judged"),
    /** The sessions it runs in (D50); empty or absent means it runs throughout / every session. */
    sessionIds: z.array(ulidSchema).optional(),
    /** How a dancer signs up across sessions (D53): one entry, or one per session. Default `once`. */
    entryMode: z.enum(COMPETITION_ENTRY_MODES).default("once"),
    startsLocal: plainTimeSchema.optional(),
    /** Its own venue — jigging in the school gym, horseshoes on the field (D20). */
    location: optionalText(shortTextSchema),
    entryLimit: z.number().int().min(1).optional(),
    /** The participant unit (D55). 1/1 is individual; a wider band is a team competition. */
    rosterMin: rosterBoundSchema.default(1),
    rosterMax: rosterBoundSchema.default(1),
    /** Target panel size (D56); absent falls back to the event's default. */
    panelSize: competitionPanelSizeSchema.optional(),
    /**
     * How somebody comes to be in it (D75).
     *
     * `automatic` is right only for the things that are true of everyone by definition — Grand
     * Entry, and little else. `chosen` by default, because the alternative to a wrong
     * `automatic` is a desk ticking a box three hundred times, and the alternative to a wrong
     * `chosen` is nobody noticing.
     */
    enrolment: z.enum(COMPETITION_ENROLMENTS).default("chosen"),
    blindJudging: z.boolean().default(false),
    sortOrder: z.number().int().min(0).default(0),
  })
  .refine(rosterOrdered, rosterOrderIssue);

/**
 * A competition edit. Nullable where a value can be removed, and absent fields are left untouched.
 * For sessions the array *is* the full restatement — an empty `sessionIds` moves a competition back
 * to "every session" (D50) — and an absent `sessionIds` leaves the set alone.
 */
export const updateCompetitionSchema = z
  .object({
    name: shortTextSchema.optional(),
    description: longTextSchema.nullable().optional(),
    type: z.enum(COMPETITION_TYPES).optional(),
    format: z.enum(COMPETITION_FORMATS).optional(),
    sessionIds: z.array(ulidSchema).optional(),
    entryMode: z.enum(COMPETITION_ENTRY_MODES).optional(),
    startsLocal: plainTimeSchema.nullable().optional(),
    location: shortTextSchema.nullable().optional(),
    entryLimit: z.number().int().min(1).nullable().optional(),
    rosterMin: rosterBoundSchema.optional(),
    rosterMax: rosterBoundSchema.optional(),
    /** `null` clears the override, returning the competition to the event default (D56). */
    panelSize: competitionPanelSizeSchema.nullable().optional(),
    /**
     * Changing this enters people from their *next* registration or edit onward, not retroactively
     * — automatic enrolment runs when a registration gains entries. Turning it on mid-event is
     * therefore a decision to check the existing list, not a backfill.
     */
    enrolment: z.enum(COMPETITION_ENROLMENTS).optional(),
    blindJudging: z.boolean().optional(),
    sortOrder: z.number().int().min(0).optional(),
  })
  .refine(rosterOrdered, rosterOrderIssue);

export const DIMENSION_KINDS = ["nominal", "numeric_range"] as const;

/**
 * A dimension value.
 *
 * `minValue`/`maxValue` are inclusive and either may be null, which is how the jigging contest's
 * unbounded "Youth" and "Adults" are expressed — the model does not require every band to have
 * two ends just because the dance programme's do (D18).
 */
export const dimensionValueSchema = z
  .object({
    id: ulidSchema.optional(),
    label: shortTextSchema,
    minValue: z.number().int().min(0).max(150).nullish(),
    maxValue: z.number().int().min(0).max(150).nullish(),
    sortOrder: z.number().int().min(0).default(0),
  })
  .refine(
    (value) => value.minValue == null || value.maxValue == null || value.minValue <= value.maxValue,
    { message: "The lower bound must not be above the upper bound", path: ["maxValue"] },
  );

/**
 * Do two bands admit the same age?
 *
 * Bounds are inclusive at both ends, so 18–55 and 55+ overlap on a single year — which is exactly
 * the kind of overlap that is invisible while writing the bands and obvious on the floor when a
 * 55-year-old appears in two categories.
 */
function bandsOverlap(
  a: { minValue?: number | null | undefined; maxValue?: number | null | undefined },
  b: { minValue?: number | null | undefined; maxValue?: number | null | undefined },
): boolean {
  const aMin = a.minValue ?? Number.NEGATIVE_INFINITY;
  const aMax = a.maxValue ?? Number.POSITIVE_INFINITY;
  const bMin = b.minValue ?? Number.NEGATIVE_INFINITY;
  const bMax = b.maxValue ?? Number.POSITIVE_INFINITY;
  return aMin <= bMax && bMin <= aMax;
}

export const createDimensionSchema = z
  .object({
    name: shortTextSchema,
    kind: z.enum(DIMENSION_KINDS),
    required: z.boolean().default(true),
    sortOrder: z.number().int().min(0).default(0),
    values: z.array(dimensionValueSchema).min(1),
  })
  .refine(
    (value) =>
      value.kind === "nominal" ||
      value.values.every((v) => v.minValue != null || v.maxValue != null),
    {
      message: "A numeric range dimension needs bounds on its values",
      path: ["values"],
    },
  )
  /**
   * Numeric bands may not overlap (D32).
   *
   * The pilot community's answer to whether Adults 18+ and Golden Age 55+ overlap was that they
   * must not — so an age resolves to exactly one band, and "which category am I in" has one answer
   * rather than a rule for choosing between two.
   *
   * Enforced here, at the point a band is written, rather than resolved later at the registration
   * desk. A dancer who satisfies two categories is a problem discovered in an arena; two bands that
   * touch is a problem discovered while typing them.
   */
  .refine(
    (dimension) => {
      if (dimension.kind !== "numeric_range") return true;
      return dimension.values.every(
        (value, index) =>
          !dimension.values.some(
            (other, otherIndex) => otherIndex > index && bandsOverlap(value, other),
          ),
      );
    },
    {
      message:
        "Two bands cover the same age. Bounds include both ends, so 18–55 and 55+ overlap on 55 — end one band a year before the next begins.",
      path: ["values"],
    },
  );

/**
 * A dimension edit, restating its values.
 *
 * Values carrying an `id` are updated in place — which is what makes renaming *Adults 18+* to
 * *Adults 18–54* keep every category and entry standing on it — and a value left out is one the
 * organiser removed.
 *
 * `kind` is absent because it is not editable: turning an age band into a nominal value would
 * strip bounds that categories and registrations already resolve against. A community that needs
 * the other kind adds a dimension and retires this one, which is the same change said out loud.
 */
export const updateDimensionSchema = z
  .object({
    name: shortTextSchema.optional(),
    required: z.boolean().optional(),
    sortOrder: z.number().int().min(0).optional(),
    values: z.array(dimensionValueSchema).min(1).optional(),
  })
  /**
   * The same no-overlap rule creation enforces (D32), applied on the way back in.
   *
   * Only bounded values are compared, which is how this holds without being told the kind: a
   * nominal dimension's values carry no bounds at all and are skipped, and a numeric one's all
   * carry at least one. Without this, the rule an organiser cannot break while typing new bands
   * could be broken by editing them a week later.
   */
  .refine(
    (dimension) => {
      const bounded = (dimension.values ?? []).filter(
        (value) => value.minValue != null || value.maxValue != null,
      );
      return bounded.every(
        (value, index) =>
          !bounded.some((other, otherIndex) => otherIndex > index && bandsOverlap(value, other)),
      );
    },
    {
      message:
        "Two bands cover the same age. Bounds include both ends, so 18–55 and 55+ overlap on 55 — end one band a year before the next begins.",
      path: ["values"],
    },
  );

/** Copy a community template's bands into this competition (D43). */
export const applyDimensionTemplateSchema = z.object({
  templateId: ulidSchema,
});

/** Keep this competition's dimension as a community template. The name defaults to the
 *  dimension's own, because "Age Group" is usually what it should be called. */
export const saveDimensionTemplateSchema = z.object({
  name: shortTextSchema.optional(),
});

/**
 * "For Age Group in 0–7, collapse Dance Style" (D18).
 *
 * One rule per (collapsed dimension, triggering value). Three youth bands collapsing style is
 * three rows, which reads oddly until you notice it is what lets a community collapse style for
 * 0–7 and 8–12 but not 13–17 without a special case.
 */
export const createCollapseRuleSchema = z.object({
  collapsedDimensionId: ulidSchema,
  whenDimensionId: ulidSchema,
  whenValueId: ulidSchema,
  /** Placeholders are dimension names, e.g. `{Gender} Combined` → "Boy's Combined". */
  labelTemplate: optionalText(shortTextSchema),
});

/**
 * What generation produces, before anything is created.
 *
 * Category generation is a **preview, never a commit** (ux.md §3.10). The organiser sees the grid
 * and merges, renames or removes; only then is it written. This schema is what the preview
 * returns and what the confirmation posts back, so the thing reviewed and the thing created are
 * literally the same object.
 */
export const generatedCategorySchema = z.object({
  name: shortTextSchema,
  /** Dimension id → the value ids it stands for; an empty array is a collapsed dimension. A
   *  generated category carries one value per non-collapsed dimension, a hand-written one may carry
   *  several (D51). */
  assignments: z.record(ulidSchema, z.array(ulidSchema)),
  entryLimit: z.number().int().min(1).optional(),
  sortOrder: z.number().int().min(0).default(0),
  /** Set when the organiser edited the proposal. Carried so the confirmation screen can show
   *  what was changed from the generated grid. */
  edited: z.boolean().default(false),
});

export const commitGeneratedCategoriesSchema = z.object({
  categories: z.array(generatedCategorySchema).min(1),
  /** Existing generated categories not in this list are archived rather than deleted, because a
   *  category may already carry entries. */
  archiveMissing: z.boolean().default(true),
});

/**
 * A category the organiser typed, rather than one generation produced (D43).
 *
 * `assignments` is optional because not every category is a point in a grid. *Tiny Tots*, *Team
 * Dance* and the one-off special announced in July are real categories with no dimensions behind
 * them, and making an organiser invent a dimension to hold one would be the model dictating the
 * programme rather than expressing it.
 *
 * Where assignments *are* given, they are what make the category visible to a prize rule scoped to
 * a band and to the age-based suggestion at the registration desk — so a hand-written category
 * that does correspond to bands should carry them.
 */
export const createCategorySchema = z.object({
  name: shortTextSchema,
  assignments: z.record(ulidSchema, z.array(ulidSchema)).optional(),
  /** Null and absent both mean no cap. A form that clears the field says so with null, and refusing
   *  that would make the same dialog send different shapes on create and on edit. */
  entryLimit: z.number().int().min(1).nullable().optional(),
  /** The session it runs in (D53), when different from its siblings; absent = follow the competition. */
  sessionId: ulidSchema.optional(),
  /** Where it sits in the arranged order (D68). Absent appends it, which is what writing a category
   *  down means — a default of 0 would put every new one first, ahead of an order somebody set. */
  sortOrder: z.number().int().min(0).optional(),
});

/** `entryLimit: null` removes a cap and `sessionId: null` returns it to following the competition
 *  (D53); an absent field leaves either alone. */
export const updateCategorySchema = createCategorySchema.partial().extend({
  entryLimit: z.number().int().min(1).nullable().optional(),
  sessionId: ulidSchema.nullable().optional(),
  status: z.enum(["draft", "open", "closed", "complete"]).optional(),
});

/**
 * The order a competition's categories are shown in (D68).
 *
 * The whole list, not a moved id and a destination: the order is a property of the list, and
 * sending it entire is what lets the server renumber from zero instead of trusting that everybody
 * else's `sortOrder` is still what the client last saw. A category left out is an order that was
 * decided against a stale list, and the repository refuses it rather than silently sorting it last.
 */
export const reorderCategoriesSchema = z.object({
  categoryIds: z.array(ulidSchema).min(1),
});

/**
 * How entrants leave one phase for the next (feature list §7).
 *
 * The union mirrors `AdvancementRule` in `@powwow/scoring-engine` member for member, because what
 * is stored in `phases.advancement_rule_json` is handed to `evaluateAdvancement` unchanged. A
 * shape accepted here that the engine cannot switch on would be a hole no test would find until a
 * tabulator pressed the button on a Saturday.
 *
 * `manual`, `organizer_approval` and `head_judge_approval` are rules that produce a *hold* rather
 * than a set — someone has to decide, and the system records that they did rather than inventing
 * an outcome.
 */
export const advancementRuleSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("all") }),
  z.object({ kind: z.literal("manual") }),
  z.object({ kind: z.literal("top_n"), count: z.number().int().min(1) }),
  z.object({ kind: z.literal("top_percent"), percent: z.number().int().min(1).max(100) }),
  z.object({ kind: z.literal("min_score"), points: z.number().int() }),
  z.object({ kind: z.literal("pass_fail") }),
  z.object({ kind: z.literal("organizer_approval") }),
  z.object({ kind: z.literal("head_judge_approval") }),
]);

export const createPhaseSchema = z.object({
  name: shortTextSchema,
  sortOrder: z.number().int().min(0).default(0),
  /** The session this round runs in (D53), when different from the category's; absent = follow it. */
  sessionId: ulidSchema.optional(),
  advancementRule: advancementRuleSchema.optional(),
});

/** `advancementRule: null` clears the rule and `sessionId: null` returns the round to following the
 *  category (D53); an absent field leaves either alone. */
export const updatePhaseSchema = z.object({
  name: shortTextSchema.optional(),
  sortOrder: z.number().int().min(0).optional(),
  sessionId: ulidSchema.nullable().optional(),
  advancementRule: advancementRuleSchema.nullable().optional(),
});

export const SCORING_METHODS = ["sum", "average", "drop_high_low", "rank", "pass_fail"] as const;

export const scoringCriterionSchema = z
  .object({
    id: ulidSchema.optional(),
    name: shortTextSchema,
    description: optionalText(longTextSchema),
    minValue: z.number().int().min(0).default(0),
    maxValue: z.number().int().min(1).default(10),
    /** Step size for the judge's stepper. Never a slider — unusable with cold hands (ux.md §2). */
    step: z.number().int().min(1).default(1),
    /** Basis points; 10000 is ×1. Integer arithmetic keeps a recomputed historical result exact. */
    weightBp: z.number().int().min(1).max(1_000_000).default(10_000),
    sortOrder: z.number().int().min(0).default(0),
  })
  .refine((value) => value.maxValue > value.minValue, {
    message: "The maximum must be above the minimum",
    path: ["maxValue"],
  })
  .refine((value) => (value.maxValue - value.minValue) % value.step === 0, {
    message: "The range must divide evenly by the step, or the top of the scale is unreachable",
    path: ["step"],
  });

/**
 * Tie-break rules, applied in order and reported in the calculation trace.
 *
 * A list rather than a single choice because ties at a pow wow are broken by convention, and the
 * conventions stack: highest single criterion, then the head judge's score, then a dance-off. The
 * last resort is explicit — someone has to decide, and the system records that they did.
 */
export const tieBreakRuleSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("highest_criterion"), criterionId: ulidSchema }),
  z.object({ kind: z.literal("highest_single_judge") }),
  z.object({ kind: z.literal("lowest_dropped_score") }),
  z.object({ kind: z.literal("head_judge"), judgeUserId: ulidSchema }),
  z.object({ kind: z.literal("manual") }),
]);

/**
 * One decision a judge may record under `pass_fail` — Pass, Advance, Not eligible, Disqualified.
 *
 * The words are the community's (D34); `passing` is the only part the engine reads. Two decisions
 * is the minimum because a scale with one outcome is not a decision.
 */
export const passFailDecisionSchema = z.object({
  id: z.string().trim().min(1).max(40),
  label: shortTextSchema,
  passing: z.boolean(),
});

export const passFailOptionsSchema = z.object({
  decisions: z.array(passFailDecisionSchema).min(2).max(20),
  /** How the panel's decisions combine: every judge, more than half, or any one of them. */
  rule: z.enum(["all", "majority", "any"]).default("majority"),
});

/** Method-specific options, stored whole in `scoring_configs.options_json`. */
export const scoringOptionsSchema = z.object({
  passFail: passFailOptionsSchema.optional(),
});

/**
 * A scoring configuration.
 *
 * The two refinements are what keep `options_json` honest: a `pass_fail` configuration is
 * meaningless without decisions to choose between, and a `sum` configuration carrying pass/fail
 * options is a leftover from a method someone changed — storing it would leave the engine reading
 * one thing while the screen showed another.
 *
 * Criteria are likewise method-bound. `pass_fail` weighs no criteria at all, so requiring one
 * would force an organiser to invent a scale nobody scores against.
 */
export const createScoringConfigSchema = z
  .object({
    name: shortTextSchema,
    method: z.enum(SCORING_METHODS).default("sum"),
    isTemplate: z.boolean().default(false),
    competitionId: ulidSchema.optional(),
    categoryId: ulidSchema.optional(),
    phaseId: ulidSchema.optional(),
    tieBreaks: z.array(tieBreakRuleSchema).default([]),
    options: scoringOptionsSchema.optional(),
    criteria: z.array(scoringCriterionSchema).default([]),
  })
  .refine((value) => value.method !== "pass_fail" || value.options?.passFail != null, {
    message: "Pass/fail scoring needs the decisions a judge may choose between",
    path: ["options", "passFail"],
  })
  .refine((value) => value.method === "pass_fail" || value.options?.passFail == null, {
    message: "Only pass/fail scoring carries decision options",
    path: ["options", "passFail"],
  })
  .refine((value) => value.method === "pass_fail" || value.criteria.length > 0, {
    message: "A scored configuration needs at least one criterion",
    path: ["criteria"],
  });

export const updateScoringConfigSchema = z.object({
  name: shortTextSchema.optional(),
  tieBreaks: z.array(tieBreakRuleSchema).optional(),
  options: scoringOptionsSchema.nullable().optional(),
});

export type AdvancementRuleInput = z.infer<typeof advancementRuleSchema>;
export type PassFailOptions = z.infer<typeof passFailOptionsSchema>;
export type ScoringOptions = z.infer<typeof scoringOptionsSchema>;
export type CreateCompetitionInput = z.infer<typeof createCompetitionSchema>;
export type CreateDimensionInput = z.infer<typeof createDimensionSchema>;
export type GeneratedCategory = z.infer<typeof generatedCategorySchema>;
export type TieBreakRule = z.infer<typeof tieBreakRuleSchema>;
export type ScoringCriterionInput = z.infer<typeof scoringCriterionSchema>;
export type ScoringMethod = (typeof SCORING_METHODS)[number];
